import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {Box, useMediaQuery} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {LottieLight, LottieLightProps} from "lottie-react";
import React, {useMemo} from "react";
import formAcceptedAnimation from "./assets/formAccepted.json";

type Rgb = [number, number, number];

type ArtworkRole = 'accent' | 'check' | 'lines' | 'paper' | 'halo';

const LottiePlayer = LottieLight as React.ComponentType<LottieLightProps>;

const ARTWORK_COLORS: [Rgb, ArtworkRole][] = [
    [[0.302, 0.451, 0.9804], 'accent'],
    [[0.2667, 0.2667, 0.2667], 'check'],
    [[0.8824, 0.8941, 0.898], 'lines'],
    [[1, 1, 1], 'paper'],
    [[0.9608, 0.9333, 0.8941], 'halo'],
];

const colorKey = (color: Rgb) => color.map(channel => channel.toFixed(4)).join(',');

const toRgb = (color: string, fallback: Rgb): Rgb => {
    const hex = color.match(/^#([\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i)?.[1];
    if (hex) {
        const expanded = hex.length <= 4
            ? hex.split('').map(digit => digit.repeat(2)).join('')
            : hex;
        return [0, 2, 4].map(index => parseInt(expanded.slice(index, index + 2), 16) / 255) as Rgb;
    }

    const rgb = color.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
    if (rgb) return rgb.slice(1, 4).map(channel => Number(channel) / 255) as Rgb;

    return fallback;
};

const blend = (foreground: Rgb, background: Rgb, opacity: number): Rgb => (
    foreground.map((channel, index) => channel * opacity + background[index] * (1 - opacity)) as Rgb
);

const recolor = (roles: Record<ArtworkRole, Rgb>): object => {
    const animation = JSON.parse(JSON.stringify(formAcceptedAnimation)) as Record<string, unknown>;
    const replacements = new Map(ARTWORK_COLORS.map(([color, role]) => [colorKey(color), roles[role]]));

    const visit = (value: unknown): void => {
        if (Array.isArray(value)) {
            value.forEach(visit);
            return;
        }
        if (!value || typeof value !== 'object') return;

        const node = value as Record<string, unknown>;
        const color = (node.ty === 'fl' || node.ty === 'st') && typeof node.c === 'object'
            ? (node.c as Record<string, unknown>).k
            : undefined;

        if (Array.isArray(color) && color.length === 3 && color.every(channel => typeof channel === 'number')) {
            const replacement = replacements.get(colorKey(color as Rgb));
            if (replacement) (node.c as Record<string, unknown>).k = replacement;
        }

        Object.values(node).forEach(visit);
    };

    visit(animation);
    return animation;
};

function SurveySuccessAnimation() {
    const theme = useTheme();
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    const animationData = useMemo(() => {
        const accent = toRgb(theme.palette.primary.main, [0.184, 0.435, 0.929]);
        const paper = toRgb(theme.palette.background.paper, [1, 1, 1]);
        const secondary = toRgb(theme.palette.text.secondary, [0.353, 0.392, 0.447]);

        return recolor({
            accent,
            check: accent,
            lines: blend(secondary, paper, 0.35),
            paper,
            halo: blend(accent, paper, 0.14),
        });
    }, [theme.palette.primary.main, theme.palette.background.paper, theme.palette.text.secondary]);

    if (prefersReducedMotion) {
        return <CheckCircleOutlineIcon aria-hidden sx={{fontSize: 72, color: 'primary.main', mx: 'auto'}}/>;
    }

    return (
        <Box aria-hidden sx={{width: {xs: 140, sm: 180}, height: {xs: 140, sm: 180}, mx: 'auto'}}>
            <LottiePlayer
                src={animationData}
                autoplay
                loop={false}
                style={{width: '100%', height: '100%'}}
            />
        </Box>
    );
}

export default SurveySuccessAnimation;
